import { z } from 'zod';
import { UuidSchema } from './primitives/uuid.js';

export const AdminDigestTimeRangeSchema = z.enum(['1h', '3h', '6h', '12h', '24h', '3d', '7d']);
export type AdminDigestTimeRange = z.infer<typeof AdminDigestTimeRangeSchema>;

export const AdminDigestReportLengthSchema = z.enum(['compact', 'normal', 'extended']);
export type AdminDigestReportLength = z.infer<typeof AdminDigestReportLengthSchema>;

export const AdminPulseEmailSettingSchema = z.object({
  enabled: z.boolean(),
  emailEnabled: z.boolean(),
  scheduleHour: z.number().int().nullable(),
  lastSentAt: z.date().nullable(),
});
export type AdminPulseEmailSetting = z.infer<typeof AdminPulseEmailSettingSchema>;

export const AdminDailyDigestSettingSchema = z.object({
  enabled: z.boolean(),
  scheduleHour: z.number().int(),
  timezone: z.string(),
  timeRange: AdminDigestTimeRangeSchema,
  reportLength: AdminDigestReportLengthSchema,
});
export type AdminDailyDigestSetting = z.infer<typeof AdminDailyDigestSettingSchema>;

export const AdminDigestSettingSchema = z.object({
  userId: z.string(),
  email: z.string().nullable(),
  name: z.string().nullable(),
  role: z.string(),
  dailyDigest: AdminDailyDigestSettingSchema.nullable(),
  pulseEmail: AdminPulseEmailSettingSchema.nullable(),
  reportLength: AdminDigestReportLengthSchema.nullable(),
});
export type AdminDigestSetting = z.infer<typeof AdminDigestSettingSchema>;

export const EmailDeliveryStatusSchema = z.enum(['reserved', 'pending', 'processing', 'sent', 'failed', 'cancelled']);
export type EmailDeliveryStatus = z.infer<typeof EmailDeliveryStatusSchema>;

export const AdminDeliveryMetaSchema = z
  .object({
    analysisId: z.string().nullable(),
    timezone: z.string().nullable(),
    scheduledHour: z.number().int().nullable(),
    timeRange: AdminDigestTimeRangeSchema.nullable(),
    reportLength: AdminDigestReportLengthSchema.nullable(),
  })
  .catchall(z.unknown())
  .nullable();
export type AdminDeliveryMeta = z.infer<typeof AdminDeliveryMetaSchema>;

export const AdminDeliverySchema = z.object({
  id: z.string(),
  userId: z.string(),
  email: z.string(),
  analysisId: z.string().nullable(),
  deliveryType: z.string(),
  deliveryStatus: EmailDeliveryStatusSchema,
  errorSummary: z.string().nullable(),
  scheduledHour: z.number().int().nullable(),
  timezone: z.string().nullable(),
  timeRange: AdminDigestTimeRangeSchema.nullable(),
  reportLength: AdminDigestReportLengthSchema.nullable(),
  messageId: z.string().nullable(),
  sentAt: z.date().nullable(),
  createdAt: z.date(),
  meta: AdminDeliveryMetaSchema,
});
export type AdminDelivery = z.infer<typeof AdminDeliverySchema>;

// ============================================================================
// Admin Digests Schemas
// ============================================================================

// List Digests
export const AdminDigestsListQuerySchema = z.object({
  limit: z.number().int().min(1).max(500).default(200),
});

export type AdminDigestsListQuery = z.infer<typeof AdminDigestsListQuerySchema>;

export const AdminDigestsListResponseSchema = z.object({
  settings: z.array(AdminDigestSettingSchema),
  deliveries: z.array(AdminDeliverySchema),
  statusByUser: z.record(z.string(), z.enum(['pending', 'running'])),
  stats: z.object({
    sentToday: z.number().int(),
    sent7d: z.number().int(),
    avgProcessingMsToday: z.number().int(),
    avgEndToSendMsToday: z.number().int(),
    pendingNow: z.number().int(),
    histogramToday: z.array(z.number().int()).length(24),
    successToday: z.number().int(),
    failToday: z.number().int(),
  }),
  topTimezones: z.record(z.string(), z.number().int()),
  peakHours: z.record(z.string(), z.number().int()),
  lastSentByUser: z.record(z.string(), z.string()),
  streakByUser: z.record(z.string(), z.number().int()),
});

export type AdminDigestsListResponse = z.infer<typeof AdminDigestsListResponseSchema>;

// Update Settings
export const AdminDigestsUpdateBodySchema = z.object({
  userId: UuidSchema,
  dailyDigest: z.unknown(),
});

export type AdminDigestsUpdateBody = z.infer<typeof AdminDigestsUpdateBodySchema>;

export const AdminDigestsUpdateResponseSchema = z.object({
  updated: z.boolean(),
  dailyDigest: z.unknown(),
});

export type AdminDigestsUpdateResponse = z.infer<typeof AdminDigestsUpdateResponseSchema>;

// Preview
export const AdminDigestsPreviewBodySchema = z.object({
  channel: z.enum(['daily-digest', 'pulse']).default('daily-digest'),
  userId: UuidSchema.optional(),
  deliveryId: UuidSchema.optional(),
  mode: z.enum(['latest', 'new']).default('latest'),
  email: z.string().email().optional(),
  options: z
    .object({
      timeRange: z.string().optional(),
      reportLength: z.string().optional(),
      maxItems: z.number().int().optional(),
      days: z.number().int().optional(),
      significance: z.number().int().optional(),
      limit: z.number().int().optional(),
      excludeFollowerChanges: z.boolean().optional(),
      excludeProfileImageOnly: z.boolean().optional(),
    })
    .optional(),
});

export type AdminDigestsPreviewBody = z.infer<typeof AdminDigestsPreviewBodySchema>;

export const AdminDigestsPreviewDailyResponseSchema = z.object({
  channel: z.literal('daily-digest'),
  subject: z.string(),
  subjectBase: z.string(),
  subjectSummary: z.string().nullable(),
  html: z.string(),
  text: z.string(),
  analysisId: z.string(),
  metadata: z.object({
    timeRange: z.string(),
    reportLength: z.string(),
    email: z.string(),
    mode: z.enum(['latest', 'new', 'delivery']),
    sourceId: UuidSchema.optional(),
  }),
});

export type AdminDigestsPreviewDailyResponse = z.infer<typeof AdminDigestsPreviewDailyResponseSchema>;

export const AdminDigestsPreviewPulseResponseSchema = z.object({
  channel: z.literal('pulse'),
  subject: z.string(),
  html: z.string(),
  text: z.string(),
  metadata: z.object({
    email: z.string(),
    maxItems: z.number().int(),
    itemCount: z.number().int(),
    days: z.number().int(),
    significance: z.number().int(),
    limit: z.number().int(),
    excludeFollowerChanges: z.boolean(),
    excludeProfileImageOnly: z.boolean(),
    sourceId: z.string().optional(),
  }),
});

export type AdminDigestsPreviewPulseResponse = z.infer<typeof AdminDigestsPreviewPulseResponseSchema>;

export const AdminDigestsPreviewResponseSchema = z.union([
  AdminDigestsPreviewDailyResponseSchema,
  AdminDigestsPreviewPulseResponseSchema,
]);

export type AdminDigestsPreviewResponse = z.infer<typeof AdminDigestsPreviewResponseSchema>;

export const AdminDigestsPreviewOptionsSchema = AdminDigestsPreviewBodySchema.shape.options;

// Send
export const AdminDigestsSendBodySchema = z.object({
  userId: UuidSchema,
  email: z.string().email().optional(),
});

export type AdminDigestsSendBody = z.infer<typeof AdminDigestsSendBodySchema>;

export const AdminDigestsSendResponseSchema = z.object({
  enqueued: z.boolean(),
});

export type AdminDigestsSendResponse = z.infer<typeof AdminDigestsSendResponseSchema>;

// Resend
export const AdminDigestsResendBodySchema = z.object({
  id: UuidSchema,
});

export type AdminDigestsResendBody = z.infer<typeof AdminDigestsResendBodySchema>;

export const AdminDigestsResendResponseSchema = z.object({
  sent: z.boolean(),
  messageId: z.string().optional(),
});

export type AdminDigestsResendResponse = z.infer<typeof AdminDigestsResendResponseSchema>;

// Test Email
export const AdminDigestsTestEmailBodySchema = z.object({
  email: z.string().email(),
});

export type AdminDigestsTestEmailBody = z.infer<typeof AdminDigestsTestEmailBodySchema>;

export const AdminDigestsTestEmailResponseSchema = z.object({
  success: z.boolean(),
  messageId: z.string().nullable(),
});

export type AdminDigestsTestEmailResponse = z.infer<typeof AdminDigestsTestEmailResponseSchema>;

// ============================================================================
// Admin Digests Routes Definitions
// ============================================================================
